import moment from 'moment-timezone';
import { BadRequestError } from '../errors';

interface ITimeSpanLike {
  start: string;
  end: string;
}

export class TimeSpan {
  public static millisecondsForMinutes(minutes: number): number {
    return minutes * 60 * 1000;
  }

  public static oneHourFrom(now: Date) {
    return moment(now).add(1, 'hours').toDate();
  }

  public static areOnSameDateSameDay(startTime: Date, date: Date): boolean {
    return (startTime.getFullYear() === date.getFullYear()
      && startTime.getMonth() === date.getMonth()
      && startTime.getDate() === date.getDate());
  }

  public static areOnSameDayInTimeZone(timeA: Date, timeB: Date, timezone: string): boolean {
    const momentA = moment(timeA).tz(timezone);
    const momentB = moment(timeB).tz(timezone);
    return momentA.isSame(momentB, 'day');
  }

  public static max(first: Date, second: Date): Date {
    return new Date(Math.max(first.getTime(), second.getTime()));
  }

  public static min(first: Date, second: Date): Date {
    return new Date(Math.min(first.getTime(), second.getTime()));
  }

  /**
   * returns a floating point number for the number of hours since midnight, where the minutes
   * since the hour determines the fractional part of the result.
   * ex 1330 would be 13.5, since it is 13 hours since midnight and 0.5 hours (30/60 minutes) since the hour
   */
  public static getDecimalHours(hhmm: string) {
    const hh = TimeSpan.getHour(hhmm);
    const mm = TimeSpan.getMinute(hhmm);
    return hh + (mm / 60);
  }

  public static getHour(hhmm: string) {
    return Number(hhmm.substr(0, 2));
  }

  public static getMinute(hhmm: string) {
    return Number(hhmm.substr(2, 2));
  }

  public static generateHHMMStringFromDate(date: Date): string {
    return TimeSpan.generateHHMMStringFromHourAndMinute(date.getHours(), date.getMinutes());
  }

  public static generateHHMMStringFromHourAndMinute(hours: number, minutes: number): string {
    return `${hours < 10 ? '0' : ''}${hours}`
      + `${minutes < 10 ? '0' : ''}${minutes}`;
  }

  public static formatHHMMAsTime(hhmm: string, format: 'h:mm A' | 'HH:mm' | 'shorthand_ampm'): string {
    const minutes = TimeSpan.getMinute(hhmm);

    const m = moment();
    m.hours(TimeSpan.getHour(hhmm));
    m.minutes(minutes);
    if (format === 'shorthand_ampm') {
      const momentFormat = (minutes === 0) ? 'ha' : 'h:mma';
      return m.format(momentFormat);
    }
    return m.format(format);
  }

  public static generateDateFromHHMMString(hhmm: string, onDate: Date = new Date()): Date {
    const returnDate = new Date(
      onDate.getFullYear(),
      onDate.getMonth(),
      onDate.getDate(),
      0,
      0,
      0,
      0,
    );
    returnDate.setHours(TimeSpan.getHour(hhmm), TimeSpan.getMinute(hhmm), 0, 0);
    return returnDate;
  }

  /**
   * You probably want to use createStartOfDayDateInTimezone instead
   */
  public static createStartOfDayDate(from: Date): Date {
    return new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate(),
      0,
      0,
      0,
      0,
    );
  }

  public static createStartOfDayDateInTimezone(from: Date, timezone: string): Date {
    return moment(from)
      .tz(timezone)
      .startOf('day')
      .toDate();
  }

  /**
   * You probably want to use createEndOfDayDateInTimezone instead
   */
  public static createEndOfDayDate(from: Date): Date {
    return new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate(),
      23,
      59,
      59,
      999,
    );
  }

  public static createEndOfDayDateInTimezone(from: Date, timezone: string): Date {
    return moment(from)
      .tz(timezone)
      .endOf('day')
      .toDate();
  }

  public static getDateAndTimeInTimezone(date: Date, timezone: string): Date {
    return moment(date)
      .tz(timezone)
      .toDate();
  }

  public static getAWeekAgo(date: Date): Date {
    const aWeekAgo = new Date(date);
    aWeekAgo.setDate(date.getDate() - 7);
    return aWeekAgo;
  }

  public static getXDaysAgo(amountOfDaysToGoBack: number, date: Date): Date {
    const daysAgo = new Date(date);
    daysAgo.setDate(date.getDate() - amountOfDaysToGoBack);
    return daysAgo;
  }

  public static getAWeekAhead(date: Date): Date {
    const aWeekAgo = new Date(date);
    aWeekAgo.setDate(date.getDate() + 7);
    return aWeekAgo;
  }

  public static getStartOfDayInTimezonePlusHours(from: Date, timezone: string, hoursToAdd: number) {
    return moment(from)
      .tz(timezone)
      .startOf('day')
      .add(hoursToAdd, 'hours')
      .toDate();
  }

  public static getStartOfISOWeekInTimezone(from: Date, timezone: string) {
    return moment(from)
      .tz(timezone)
      .startOf('isoWeek')
      .toDate();
  }

  public static getEndOfISOWeekInTimezone(from: Date, timezone: string) {
    return moment(from)
      .tz(timezone)
      .endOf('isoWeek')
      .toDate();
  }

  public static getFirstDateOfNextMonth(date: Date): number {
    let firstDateNextMonth;
    if (date.getMonth() === 11) {
      firstDateNextMonth = new Date(date.getFullYear() + 1, 0, 1);
    } else {
      firstDateNextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    firstDateNextMonth.setHours(1, 0, 0, 0);
    return firstDateNextMonth.getTime();
  }

  public static getNextMonday(date: Date): number {
    const monday = 1;
    date.setDate(date.getDate() + ((7 + monday - date.getDay() - 1) % 7) + 1);
    date.setHours(1, 0, 0, 0);
    return date.getTime();
  }

  public static getAddedDurationInUnitsFromDate(from: Date, duration: number, unit: string): Date {
    return moment(from)
      .add(duration, unit as moment.unitOfTime.DurationConstructor)
      .set('hour', 0)
      .set('minute', 30)
      .set('second', 0)
      .set('millisecond', 0)
      .toDate();
  }

  public static getNowInTimeZone(timezone: string): Date {
    return moment.tz(timezone).toDate();
  }

  public static getEndOfDayDateInTwoWeeks(timezone: string): Date {
    return moment.tz(timezone).add(14, 'days').endOf('day').toDate();
  }

  public static getDateAtStartOfNextMinute(date: Date): Date {
    return moment(date).add(1, 'minutes').startOf('minute').toDate();
  }

  public static createOnTheHourSpans(start: string, end: string): TimeSpan[] {
    if (end < start) {
      throw new BadRequestError('Cannot build hour spans that end before it starts');
    }
    let next = start;

    const result: TimeSpan[] = [];
    while (next < end) {
      const ends = TimeSpan.generateHHMMStringFromHourAndMinute(TimeSpan.getHour(next) + 1, 0);
      result.push(new TimeSpan(next, ends > end ? end : ends));
      next = ends;
    }
    return result;
  }

  public static firstItemWithRangeCovering<T extends ITimeSpanLike>(items: T[], hhmm: string): T | undefined {
    return items.find((item: T) => hhmm >= item.start && hhmm < item.end);
  }

  public static getOnStartOfHour(hhmm: string, roundTo: 'sameHour' | 'nextHour') {
    const minutes = TimeSpan.getMinute(hhmm);
    if (minutes === 0) {
      return hhmm;
    }

    const hours = TimeSpan.getHour(hhmm);
    const roundedHour = roundTo === 'sameHour'
      ? hours
      : (hours + 1) % 24;
    return TimeSpan.generateHHMMStringFromHourAndMinute(roundedHour, 0);
  }

  public static isSpecificHourInTimeZone(hour: number, timezone: string): boolean {
    const now = moment.tz(timezone);
    return now.hour() === hour;
  }

  public static getItemRange<T extends ITimeSpanLike>(items: T[]): TimeSpan {
    const { start } = items[0];
    const { end } = items[items.length - 1];
    return new TimeSpan(start, end);
  }

  public static merge<T extends ITimeSpanLike>(
    items: T[],
    similar: (a: T, b: T) => boolean,
  ): T[] {
    const result: T[] = [];
    let last: T | undefined;
    for (const item of items) {
      if (item.start === last?.end && similar(item, last)) {
        last.end = item.end;
      } else {
        last = item;
        result.push(item);
      }
    }
    return result;
  }

  constructor(
    public readonly start: string,
    public readonly end: string,
  ) { /**/ }

  public isTimeInRange(date: Date): boolean {
    return (TimeSpan.generateDateFromHHMMString(this.start, date).getTime() <= date.getTime()
      && TimeSpan.generateDateFromHHMMString(this.end, date).getTime() > date.getTime());
  }
}
